import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, setAuthToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Trash2, Loader2, LogOut } from "lucide-react";

export default function DeleteAccount() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const signOutEverywhere = async () => {
    setSigningOut(true);
    try {
      await api.post("/auth/logout-all");
      setAuthToken(null);
      toast.success("Signed out on every device");
      await logout();
      nav("/login");
    } catch {
      toast.error("Couldn't sign out everywhere");
      setSigningOut(false);
    }
  };

  const destroy = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account");
      setAuthToken(null);
      toast.success("Your account and all data were deleted.");
      await logout();
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete account — email support@refundbot.org");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 pt-6 border-t border-dashed border-[#282C30] flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="danger-zone">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872]">PRIVACY · DANGER ZONE</div>
        <div className="text-sm text-[#A0A7B0] mt-1">Sign out of all devices, or delete your account, disconnect Gmail and erase every leak, refund and claim we hold.</div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button variant="outline" onClick={signOutEverywhere} disabled={signingOut} className="btn-outline-paper" data-testid="logout-all-button">
          {signingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-1.5" /> Sign out everywhere</>}
        </Button>
        <Button variant="outline" onClick={() => setOpen(true)} className="border-[#FF5C5C]/40 text-[#FF5C5C] hover:bg-[#FF5C5C]/10 hover:text-[#FF5C5C]" data-testid="delete-account-button">
          <Trash2 className="h-4 w-4 mr-1.5" /> Delete my account &amp; data
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141618] border-[#282C30] text-[#F4F1EA] max-w-md" data-testid="delete-account-modal">
          <DialogHeader>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5C5C]">PERMANENT</div>
            <DialogTitle className="font-display text-2xl tracking-tight">Delete everything?</DialogTitle>
            <DialogDescription className="text-[#A0A7B0]">
              We'll revoke our Gmail access, cancel any active membership, and erase your account, leaks, refunds, claims and briefs immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-[#A0A7B0]">Type <span className="font-mono text-[#F4F1EA]">DELETE</span> to confirm.</div>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" className="bg-[#0C0D0E] border-[#282C30] font-mono" data-testid="delete-account-confirm-input" />
          <div className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="btn-outline-paper flex-1" data-testid="delete-account-cancel">Keep my account</Button>
            <Button onClick={destroy} disabled={confirm !== "DELETE" || busy} className="flex-1 bg-[#FF5C5C] hover:bg-[#ff7676] text-[#1a0000]" data-testid="delete-account-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete forever"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
